import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookmarkPlus, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipeSaver = ({ token }) => {
  const { t } = useTranslation();
  const [recipes, setRecipes] = useState([]);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [newRecipe, setNewRecipe] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data.recipes);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const saveRecipe = async () => {
    if (!newRecipe.name) return;
    
    try {
      await axios.post(
        `${API}/recipes/save`,
        newRecipe,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewRecipe({ name: '', description: '' });
      setShowSaveForm(false);
      await fetchRecipes();
      alert('Recipe saved!');
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Failed to save recipe');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="recipe-saver">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Heart className="text-[#FF6600]" size={20} />
          {t('recipeSave.title')}
        </h2>
        <button
          onClick={() => setShowSaveForm(!showSaveForm)}
          className="px-4 py-2 bg-[#FF6600] text-white rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-[#E55C00]"
        >
          <BookmarkPlus size={16} />
          {t('recipeSave.addRecipe')}
        </button>
      </div>

      {showSaveForm && (
        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <input
            type="text"
            value={newRecipe.name}
            onChange={(e) => setNewRecipe({ ...newRecipe, name: e.target.value })}
            className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 focus:outline-none focus:border-[#FF6600]"
            placeholder={t('recipeSave.recipeName')}
          />
          <textarea
            value={newRecipe.description}
            onChange={(e) => setNewRecipe({ ...newRecipe, description: e.target.value })}
            className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 focus:outline-none focus:border-[#FF6600]"
            placeholder={t('recipeSave.description')}
            rows={3}
          />
          <div className="flex gap-2">
            <button onClick={saveRecipe} className="flex-1 py-3 bg-[#FF6600] text-white rounded-lg font-semibold hover:bg-[#E55C00]">
              {t('recipeSave.save')}
            </button>
            <button 
              onClick={() => setShowSaveForm(false)} 
              className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:bg-gray-900"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {recipes.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
          <Heart size={36} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">{t('recipeSave.noRecipes')}</p>
          <p className="text-xs text-gray-400">Start saving your favorite meals!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recipes.map((recipe) => (
            <div key={recipe.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-[#FF6600] transition-all">
              <h3 className="font-bold mb-2">{recipe.name}</h3>
              {recipe.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{recipe.description}</p>
              )}
              {recipe.analysis && (
                <div className="mt-3 flex gap-4 text-sm">
                  <span>{Math.round(recipe.analysis.total_calories)} cal</span>
                  <span>{Math.round(recipe.analysis.total_protein)}g protein</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecipeSaver;
